import React, {useEffect, useState} from 'react';
import {Button, Form, Input, Modal, Spin, Table} from "antd";
import {FormattedMessage, useIntl} from "react-intl";
import {Condition, ConditionCreate, ConditionUpdate} from "../../../api-client";
import {DeleteOutlined, EditOutlined} from "@ant-design/icons";
import {isMobileOnly} from "react-device-detect";
import {DentobookApplicationContext} from "../../../context/DentobookApplicationContext";
import {getPatientApi} from "../../../api/apiInstances";
import logger from "../../../utils/logger";

interface ClinicGeneralCardProps {
    showModal: boolean;
    onClose: () => void;
    keyword: string;
    type: string;
}

const ClinicGeneralCard: React.FC<ClinicGeneralCardProps> = ({
                                                                 type, showModal, onClose, keyword
                                                             }) => {
    const intl = useIntl();
    const [newConditionTitle, setNewConditionTitle] = useState<string>('');
    const [editConditionId, setEditConditionId] = useState<string>('');
    const [editMode, setEditMode] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [conditions, setConditions] = useState<Condition[]>([]);
    const [displayedConditions, setDisplayedConditions] = useState<Condition[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const { userOrganization } = React.useContext(DentobookApplicationContext);

    const patientApi = getPatientApi();

    const showEditModal = (condition: Condition) => {
        if (condition) {
            setEditConditionId(condition.id ?? '');
            setNewConditionTitle(condition.displayName ?? '');
            setEditMode(true);
        }
    };

    useEffect(() => {
        loadData();
    }, [type]);

    const loadData = () => {
        setLoading(true);
        patientApi.getAllConditions()
            .then(response => {
                const filteredConditions = response.data.filter(condition => condition.type === type);
                setConditions(filteredConditions);
                setDisplayedConditions(filteredConditions);
            })
            .catch(error => logger.error("Failed to load conditions:", error))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (keyword) {
            const filteredConditions = conditions.filter(condition =>
                (condition.displayName ?? '').toLowerCase().includes(keyword.toLowerCase())
            );
            setDisplayedConditions(filteredConditions);
        } else {
            setDisplayedConditions(conditions);
        }
    }, [conditions, keyword]);

    const showDeleteModal = (ConditionId: string) => {
        setEditConditionId(ConditionId);
        setIsDeleteModalVisible(true);
    };

    const resetState = () => {
        setEditConditionId('');
        setNewConditionTitle('');
        setEditMode(false);
        setIsDeleteModalVisible(false);
    };


    const closeAddConditionModal = () => {
        onClose();
    };

    const handleCancel = () => {
        closeAddConditionModal();
        setEditConditionId('');
        setNewConditionTitle('');
        setEditMode(false);
    };

    const handleAddOk = () => {
        handleCreateCondition();
        closeAddConditionModal();
    };

    const handleCreateCondition = async () => {
        if (!userOrganization?.id) return;

        // Set both title and titleEn every time, ensuring neither is inadvertently set to undefined
        const titleEnglish = userOrganization?.country !== 'BG' ? newConditionTitle : "";

        const conditionToCreate: ConditionCreate = {
            title: newConditionTitle,
            titleEnglish: titleEnglish,
            type: type,
            organizationId: userOrganization.id
        };

        try {
            await patientApi.createCondition(conditionToCreate);
            loadData();
        } catch (error) {
            logger.error("Failed to create Condition:", error);
        }
        resetState();
    };

    const handleEditConditionOk = async () => {
        const titleEnglish = userOrganization?.country !== 'BG' ? newConditionTitle : "";

        const conditionUpdate: ConditionUpdate = {
            title: newConditionTitle,
            titleEnglish: titleEnglish
        };

        try {
            if (editConditionId != null) {
                await patientApi.updateCondition(editConditionId, conditionUpdate);
            }
            loadData();
        } catch (error) {
            logger.error("Failed to edit Condition:", error);
        }
        resetState();
    };



    const handleEditConditionCancel = () => {
        resetState();
    };

    const handleDeleteConditionOk = async () => {
        try {
            if (editConditionId != null) {
                await patientApi.deleteCondition(editConditionId);
            }
            loadData();
        } catch (error) {
            logger.error("Failed to delete condition:", error);
        }
        resetState();
    };

    const handleDeleteConditionCancel = () => {
        resetState();
    };

    const getLabel = (type: string): string => {
        switch (type.toLowerCase()) {
            case 'illness':
                return intl.formatMessage({ id: 'illnesses' });
            case 'allergy':
                return intl.formatMessage({ id: 'allergies' });
            case 'medication':
                return intl.formatMessage({ id: 'medication' });
            default:
                return intl.formatMessage({ id: 'medication' });
        }
    };

    const typeCamelCase = type.toString().toUpperCase()[0] + type.toString().toLowerCase().substring(1);

    const modalContent = <div className="medical-history-modal-group">
        <div className="input-group">
            <Form.Item
                className={"dentobook-form-item dentobook-simple-input"}>
                <Input
                    value={getLabel(typeCamelCase)}
                    disabled
                    className="custom-input"/>
            </Form.Item>
        </div>
        <div className="input-group">
            <Form layout="vertical">
                <Form.Item
                    className={"dentobook-form-item dentobook-simple-input"}
                    label={<FormattedMessage id="naming"/>}>
                    <Input
                        value={newConditionTitle}
                        onChange={(e) => setNewConditionTitle(e.target.value)}
                        className="custom-input"
                    />
                </Form.Item>
            </Form>
        </div>
    </div>;

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin tip={<FormattedMessage id="loading" />} />
            </div>
        );
    }

    return (
        <div style={isMobileOnly ? {height: '40vh', width: 'auto', overflow: 'scroll', marginBottom: 100} : {}}>
            <Table dataSource={displayedConditions}
                   pagination={false}
                   className="dentobook-table"
                   style={{marginTop: '5px'}}>
                <Table.Column key="id"
                              width={800}
                              className={'custom-column-style'}
                              title={<FormattedMessage id={type.toString().toLowerCase()}/>}
                              render={(condition) => <span className="font-weight-500">
                            {condition.displayName}
                          </span>}
                              sorter={(medicationOne: Condition, medicationTwo: Condition) => (medicationTwo.displayName ?? '').localeCompare(medicationOne.displayName ?? '')}
                />
                <Table.Column key="actions"
                              width={isMobileOnly ? 500 : 100}
                              title={<div></div>}
                              className={'custom-column-style'}
                              render={(Condition) => {
                                  return (
                                      <div style={{display: 'unset', alignItems: 'right'}}>
                                          <Button className="text-black font-20 ml-1" type="link"
                                                  icon={<EditOutlined/>}
                                                  style={{marginRight: '10px'}}
                                                  onClick={() => showEditModal(Condition)}/>

                                          <Button className="text-black font-20" type="link"
                                                  icon={<DeleteOutlined/>}
                                                  onClick={() => showDeleteModal(Condition.id as string)}/>
                                      </div>
                                  );
                              }}/>
            </Table><Modal
            transitionName=""
            okButtonProps={{
                className: "modal-btn"
            }}
            cancelButtonProps={{
                className: "modal-btn"
            }}
            title={<FormattedMessage id={"add" + typeCamelCase}/>}
            okText={intl.formatMessage({id: 'save'})}
            cancelText={intl.formatMessage({id: 'cancel'})}
            open={showModal}
            onOk={handleAddOk}
            onCancel={handleCancel}
            className="dentobook-modal"
        >
            {modalContent}
        </Modal><Modal
            transitionName=""
            okButtonProps={{
                className: "modal-btn"
            }}
            cancelButtonProps={{
                className: "modal-btn"
            }}
            title={<FormattedMessage id={'edit' + typeCamelCase}/>}
            okText={intl.formatMessage({id: 'save'})}
            cancelText={intl.formatMessage({id: 'cancel'})}
            open={editMode}
            onOk={handleEditConditionOk}
            onCancel={handleEditConditionCancel}
            className="dentobook-modal"
        >
            {modalContent}
        </Modal>

            {/* Delete Modal */}
            <Modal
                transitionName=""
                okButtonProps={{
                    className: "modal-btn",
                    danger: true,
                    type: "primary",
                }}
                cancelButtonProps={{
                    className: "modal-btn"
                }}
                title={<FormattedMessage id={'delete' + typeCamelCase}/>}
                okText={intl.formatMessage({id: 'delete'})}
                cancelText={intl.formatMessage({id: 'cancel'})}
                open={isDeleteModalVisible} onOk={handleDeleteConditionOk}
                onCancel={handleDeleteConditionCancel}
                className="dentobook-modal"
            >
                <p><FormattedMessage id={type.toString().toLowerCase() + "DeleteConfirmation"}/></p>
            </Modal>
        </div>
    );
};
export default ClinicGeneralCard;
